import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { trackPorCodigo, trackPorId } from 'src/app/services/odoo/odoo-maestros-formato';
import {
  OdooEstadoUi, OdooFilaMaestro, OdooListaFiltro, OdooMovimiento, OdooOpcionComercio, OdooPaginaLista,
} from 'src/app/services/odoo/odoo-maestros.models';
import { OdooMaestrosService } from 'src/app/services/odoo/odoo-maestros.service';
import { OdooCarga } from 'src/app/services/odoo/odoo.models';
import { conCarga } from 'src/app/services/odoo/odoo.service';

const fNumero = new Intl.NumberFormat('es-VE');

/**
 * PESTAÑA CLIENTES. Búsqueda con espera de 300 ms, filtros Estado y Comercio, paginación en el servidor.
 * Al tocar una fila se abre el detalle debajo con los últimos movimientos (una consulta liviana).
 */
@Component({
  selector: 'app-odoo-clientes',
  templateUrl: './odoo-clientes.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooClientesComponent implements OnDestroy {
  readonly limite = 20;
  filtro: OdooListaFiltro = { q: '', estado: '', comercio: '', page: 1, limit: this.limite };
  readonly buscar = new FormControl('');
  readonly estados: { valor: OdooEstadoUi | ''; texto: string }[] = [
    { valor: '', texto: 'Todos' },
    { valor: 'anotado', texto: 'Anotado' },
    { valor: 'esperando', texto: 'Esperando envío' },
    { valor: 'problema', texto: 'Con problema' },
  ];
  readonly esperaSkeleton = [0, 1, 2, 3, 4];
  seleccionado: OdooFilaMaestro | null = null;

  private readonly params$ = new BehaviorSubject<OdooListaFiltro>(this.filtro);
  private readonly sel$ = new BehaviorSubject<string | null>(null);
  readonly lista$: Observable<OdooCarga<OdooPaginaLista<OdooFilaMaestro>>> = conCarga(this.params$, (f) => this.api.clientes(f));
  readonly comercios$: Observable<OdooOpcionComercio[]> = this.api.opcionesComercio().pipe(catchError(() => of([] as OdooOpcionComercio[])));
  readonly movimientos$: Observable<OdooCarga<OdooMovimiento[]>> = conCarga(
    this.sel$.pipe(filter((id): id is string => !!id)), (id) => this.api.movimientosCliente(id, 5));
  readonly trackFila = trackPorCodigo;
  readonly porIndice = (i: number): number => i;
  readonly porValor = (_: number, e: { valor: string }): string => e.valor;
  readonly porCode = (_: number, c: { code: string }): string => c.code;
  readonly esperaMov = [0, 1];
  readonly trackMov = trackPorId;
  private readonly sub: Subscription;

  constructor(private api: OdooMaestrosService) {
    this.sub = this.buscar.valueChanges.pipe(
      map((v) => String(v || '').trim()), debounceTime(300), distinctUntilChanged(),
    ).subscribe((q) => this.cambiar({ q, page: 1 }));
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  private cambiar(parcial: Partial<OdooListaFiltro>): void {
    this.filtro = { ...this.filtro, ...parcial };
    this.params$.next(this.filtro);
  }

  elegirEstado(valor: string): void { this.cambiar({ estado: valor as OdooEstadoUi | '', page: 1 }); }
  elegirComercio(valor: string): void { this.cambiar({ comercio: valor, page: 1 }); }
  pagina(n: number): void { this.cambiar({ page: n }); }
  recargar(): void { this.params$.next({ ...this.filtro }); }

  totalPaginas(total: number): number { return Math.max(1, Math.ceil(total / this.limite)); }
  numero(n: number): string { return fNumero.format(n); }
  desde(p: OdooPaginaLista<unknown>): number { return p.total ? (p.page - 1) * p.limit + 1 : 0; }
  hasta(p: OdooPaginaLista<unknown>): number { return Math.min(p.total, p.page * p.limit); }

  elegir(f: OdooFilaMaestro): void {
    if (this.seleccionado?.source_id === f.source_id) return;
    this.seleccionado = f;
    this.sel$.next(f.source_id);
  }
}
